import React, { useState, useEffect } from "react";
import ApiService from "../services/ApiService";
import "./BookingSlotManagement.css";

const BookingSlotManagement = () => {
  const [bookingSlots, setBookingSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [formData, setFormData] = useState({
    startTime: "",
    endTime: "",
    maxBookings: 2,
    displayOrder: 0,
    isActive: true,
  });

  useEffect(() => {
    fetchBookingSlots();
  }, []);

  const fetchBookingSlots = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getAllBookingSlots();
      setBookingSlots(response.data || []);
      setError("");
    } catch (error) {
      console.error("Error fetching booking slots:", error);
      setError("Failed to fetch booking slots");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      startTime: "",
      endTime: "",
      maxBookings: 2,
      displayOrder: 0,
      isActive: true,
    });
    setShowAddModal(false);
    setShowEditModal(false);
    setEditingSlot(null);
    setError("");
    setSuccess("");
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleAddSlot = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await ApiService.createBookingSlot(formData);
      setSuccess("Booking slot created successfully!");
      await fetchBookingSlots();
      resetForm();
    } catch (error) {
      console.error("Error creating booking slot:", error);
      setError(error.message || "Failed to create booking slot");
    } finally {
      setLoading(false);
    }
  };

  const handleEditSlot = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await ApiService.updateBookingSlot(editingSlot.id, formData);
      setSuccess("Booking slot updated successfully!");
      await fetchBookingSlots();
      resetForm();
    } catch (error) {
      console.error("Error updating booking slot:", error);
      setError(error.message || "Failed to update booking slot");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSlot = async (id) => {
    if (!window.confirm("Are you sure you want to delete this booking slot?")) {
      return;
    }

    try {
      setLoading(true);
      await ApiService.deleteBookingSlot(id);
      setSuccess("Booking slot deleted successfully!");
      await fetchBookingSlots();
    } catch (error) {
      console.error("Error deleting booking slot:", error);
      setError(error.message || "Failed to delete booking slot");
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (slot) => {
    setEditingSlot(slot);
    setFormData({
      startTime: slot.startTime,
      endTime: slot.endTime,
      maxBookings: slot.maxBookings,
      displayOrder: slot.displayOrder,
      isActive: slot.isActive,
    });
    setShowEditModal(true);
  };

  const formatTime = (timeStr) => {
    return timeStr;
  };
  return (
    <div className="booking-slot-management dark-theme">
      <div className="admin-header-section">
        <h1>Booking Slot Management</h1>
        <p>Manage time slots for booking appointments</p>
        <button onClick={() => setShowAddModal(true)} className="add-slot-btn">
          <span>➕</span>
          Add New Time Slot
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading booking slots...</p>
        </div>
      ) : (
        <div className="slots-grid">
          {bookingSlots.length === 0 ? (
            <div className="no-slots-message">
              <h3>No booking slots found</h3>
              <p>Create your first time slot to get started.</p>
            </div>
          ) : (
            bookingSlots.map((slot) => (
              <div
                key={slot.id}
                className={`slot-card ${!slot.isActive ? "inactive" : ""}`}
              >
                <div className="slot-header">
                  <h3>
                    {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                  </h3>
                  <div className="slot-status">
                    <span
                      className={`status-badge ${
                        slot.isActive ? "active" : "inactive"
                      }`}
                    >
                      {slot.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>

                <div className="slot-details">
                  <div className="detail-item">
                    <span className="label">Max Bookings:</span>
                    <span className="value">{slot.maxBookings}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Display Order:</span>
                    <span className="value">{slot.displayOrder}</span>
                  </div>
                </div>

                <div className="slot-actions">
                  <button
                    onClick={() => openEditModal(slot)}
                    className="edit-btn"
                  >
                    <span>✏️</span>
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteSlot(slot.id)}
                    className="delete-btn"
                  >
                    <span>🗑️</span>
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Add Slot Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Add New Booking Slot</h2>
              <button onClick={resetForm} className="close-btn">
                ×
              </button>
            </div>
            <form onSubmit={handleAddSlot} className="slot-form">
              <div className="form-group">
                <label htmlFor="startTime">Start Time *</label>
                <input
                  type="time"
                  id="startTime"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="endTime">End Time *</label>
                <input
                  type="time"
                  id="endTime"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="maxBookings">Max Bookings *</label>
                <input
                  type="number"
                  id="maxBookings"
                  name="maxBookings"
                  value={formData.maxBookings}
                  onChange={handleInputChange}
                  min="1"
                  max="10"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="displayOrder">Display Order</label>
                <input
                  type="number"
                  id="displayOrder"
                  name="displayOrder"
                  value={formData.displayOrder}
                  onChange={handleInputChange}
                  min="0"
                />
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  Active
                </label>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={resetForm}
                  className="cancel-btn"
                >
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="save-btn">
                  {loading ? "Creating..." : "Create Slot"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Slot Modal */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Edit Booking Slot</h2>
              <button onClick={resetForm} className="close-btn">
                ×
              </button>
            </div>
            <form onSubmit={handleEditSlot} className="slot-form">
              <div className="form-group">
                <label htmlFor="startTime">Start Time *</label>
                <input
                  type="time"
                  id="startTime"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="endTime">End Time *</label>
                <input
                  type="time"
                  id="endTime"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="maxBookings">Max Bookings *</label>
                <input
                  type="number"
                  id="maxBookings"
                  name="maxBookings"
                  value={formData.maxBookings}
                  onChange={handleInputChange}
                  min="1"
                  max="10"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="displayOrder">Display Order</label>
                <input
                  type="number"
                  id="displayOrder"
                  name="displayOrder"
                  value={formData.displayOrder}
                  onChange={handleInputChange}
                  min="0"
                />
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  Active
                </label>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={resetForm}
                  className="cancel-btn"
                >
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="save-btn">
                  {loading ? "Updating..." : "Update Slot"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingSlotManagement;
